import { AppIconModel } from "client_legacy/AppIcon/common";
import {
	Checkbox,
	Form,
	FormItem,
	Tabs,
	TextInput,
	ValidationError
} from "client/Components";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	attachToUserAndUserGroupCollection
} from "client/Features/Users/UserAndUserGroupCollection/attachable";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import {
	ActiveTab
} from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	useState
} from "react";
import { AppColorKind, AppColorViewModel } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { AppColorSelectFormItem } from "../../AppColorSelectFormItem";
import { AppIconSelector, convertColorToViewModel } from "../../AppIconSelector";
import { ConditionalAccess } from "./ConditionalAccess";
import styles from "./EditAppBase.module.less";
import { MaintenanceWindow } from "./MaintenanceWindow";
import {
	EditAppModel,
	EditGenericAppViewModel,
	SaveMaintenanceSettingsModel,
	SaveAppConditionalAccessSettingsModel,
	SaveAppTargetModel,
	SaveAppTargetType,
	SaveGenericAppModel
} from "client/Api/Apps/EditApp/saveApp";
import { AppTarget } from "./EditAppDialogContent";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import classNames from "classnames";
import { AppTagSelect } from "./AppTagSelect";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";

type WithConditionalAccess = {
	conditionalAccessSettings: SaveAppConditionalAccessSettingsModel;
};

type WithoutConditionalAccess = {
	conditionalAccessSettings?: nil;
};

type WithMaintenanceWindow = {
	maintenanceSettings: SaveMaintenanceSettingsModel;
};

type WithoutMaintenanceWindow = {
	maintenanceSettings?: nil;
};

export type EditAppBaseProps = {
	formHandler: IObjectInputAttachable<
		SaveGenericAppModel
		& (WithConditionalAccess | WithoutConditionalAccess)
		& (WithMaintenanceWindow | WithoutMaintenanceWindow)
	>;
	hasValidationErrorForSpecificSettings: boolean | undefined;
	initialAppSettings: EditGenericAppViewModel;
	initiallyActivateWhoHasAccessTab: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export const EditAppBase = (
	props: PropsWithChildren<EditAppBaseProps>
): ReactElement => {
	const {
		formHandler,
		hasValidationErrorForSpecificSettings,
		initialAppSettings,
		initiallyActivateWhoHasAccessTab,
		usersAndUserGroupsData,
		children
	} = props;

	const [activeTabId, setActiveTabId] = useState<TabId>(
		initiallyActivateWhoHasAccessTab
			? whoHasAccessTabId
			: settingsTabId
	);

	const [icon, setIcon] = useState<AppIconModel>(
		(): AppIconModel => {

			const {
				image,
				imageDisplayInfo
			} = initialAppSettings;

			return {
				id: image.id,
				url: imageDisplayInfo.imageUrl,
				type: image.type,
				renderType: imageDisplayInfo.renderType
			};
		}
	);

	const userTimezone: TimeZone = useUserTimezone();

	const actualColor: AppColorViewModel | null = convertColorToViewModel(
		props.formHandler
			.value
			.color,
		initialAppSettings.inheritedColor
	);

	const hasValidationErrorInSettingsTab: boolean | undefined = hasValidationErrorForSpecificSettings
		|| formHandler
			.under(
				"image",
				"color",
				"name"
			)
			.attachToTab()
			.hasValidationError;

	return (
		<Form onSubmit="NONE" height="fill">
			<Tabs<TabId>
				activeTabId={activeTabId}
				onActiveTabIdChange={setActiveTabId}
				height="fill"
			>
				<Tabs.Tab
					id={settingsTabId}
					title={localize("_Settings")}
					hasValidationError={hasValidationErrorInSettingsTab}
				>
					<div className={styles.tab}>
						<div
							className={classNames(
								styles.content,
								styles.content_widthConstrainted
							)}
						>
							<FormItem label={localize("_Icon")}>
								<AppIconSelector
									color={actualColor}
									value={icon}
									onChange={x => {
										formHandler.for("image")
											.reducer
											.setValue({
												id: x.id,
												type: x.type
											});

										setIcon(x);
									}}
									trackingId={
										formHandler.for("image")
											.reducer
											.path
									}
								/>
								<ValidationError
									{...formHandler.under("image")
										.attachToValidationError()
									}
								/>
							</FormItem>

							<AppColorSelectFormItem
								attachable={formHandler.for("color")}
							/>

							<FormItem
								label={localize("_AppName")}
							>
								<TextInput
									{...formHandler.for("name")
										.attachToTextInput()
									}
								/>
								<ValidationError
									{...formHandler.for("name")
										.attachToValidationError()
									}
								/>
							</FormItem>

							<FormItem label={localize("_Tags")}>
								<AppTagSelect attachable={formHandler.for("tags")} />
								<ValidationError {...formHandler.for("tags").attachToValidationError()} />
							</FormItem>

							{children}
						</div>
					</div>
				</Tabs.Tab>

				<Tabs.Tab
					{...formHandler.under("everyoneHasAccess", "whoHasAccess")
						.attachToTab()
					}
					id={whoHasAccessTabId}
					title={localize("_WhoHasAccess")}
				>
					<div className={styles.tab}>
						<div className={styles.content}>
							<FormItem>
								<Checkbox
									{...formHandler.for("everyoneHasAccess")
										.attachToCheckbox()
									}
									text={localize("_EveryoneHasAccess")}
								/>

								<ValidationError
									{...formHandler
										.for("everyoneHasAccess")
										.attachToValidationError()
									}
								/>
							</FormItem>

							{!formHandler.value.everyoneHasAccess && (
								<FormItem height="stretch">
									<UserAndUserGroupCollection
										activeTab={ActiveTab.UserGroup}
										{...attachToUserAndUserGroupCollection(
											formHandler.for("whoHasAccess"),
											{
												kind: UserAndUserGroupKind.Edit,
												dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
											}
										)}
										allUsersAndUserGroupsData={usersAndUserGroupsData}
										requiredUserLicense={initialAppSettings.userLicense}
									/>

									<ValidationError
										{...formHandler
											.for("whoHasAccess")
											.attachToValidationError()
										}
									/>
								</FormItem>
							)}
						</div>
					</div>
				</Tabs.Tab>

				<Tabs.Tab
					{...formHandler.under("owners").attachToTab()}
					id={ownersTabId}
					title={localize("_Owners")}
				>
					<div className={styles.tab}>
						<div className={styles.content}>
							<FormItem height="stretch">
								<UserAndUserGroupCollection
									activeTab={ActiveTab.UserGroup}
									{...attachToUserAndUserGroupCollection(
										formHandler.for("owners"),
										{
											kind: UserAndUserGroupKind.Edit,
											dialogTitleWhenAddingUsersAndUserGroups: localize("_AddOwners")
										}
									)}
									allUsersAndUserGroupsData={usersAndUserGroupsData}
									requiredUserLicense={initialAppSettings.userLicense}
								/>

								<ValidationError
									{...formHandler
										.for("owners")
										.attachToValidationError()
									}
								/>
							</FormItem>
						</div>
					</div>
				</Tabs.Tab>

				{formHandler.whenType(
					(x): x is WithMaintenanceWindow & typeof x => isNotNil(x.maintenanceSettings),
					(x) => (
						<Tabs.Tab
							{...x
								.under("maintenanceSettings")
								.attachToTab()
							}
							id={maintenanceTabId}
							title={localize("_Maintenance")}
						>
							<div className={styles.tab}>
								<div
									className={classNames(
										styles.content,
										styles.content_widthConstrainted
									)}
								>
									<MaintenanceWindow
										attachable={x.for("maintenanceSettings")}
										timezone={userTimezone}
									/>
								</div>
							</div>
						</Tabs.Tab>
					)
				)}

				{formHandler.whenType(
					(x): x is WithConditionalAccess & typeof x => isNotNil(x.conditionalAccessSettings),
					(x) => (
						<Tabs.Tab
							{...x
								.under("conditionalAccessSettings")
								.attachToTab()
							}
							id={conditionalAccessTabId}
							title={localize("_ConditionalAccess")}
						>
							<div className={styles.tab}>
								<div
									className={classNames(
										styles.content,
										styles.content_widthConstrainted
									)}
								>
									<ConditionalAccess
										appType={initialAppSettings.type}
										attachable={x.for("conditionalAccessSettings")}
									/>
								</div>
							</div>
						</Tabs.Tab>
					)
				)}

			</Tabs>
		</Form>
	);
};
EditAppBase.displayName = "EditAppBase";

type TabId =
	| typeof settingsTabId
	| typeof whoHasAccessTabId
	| typeof ownersTabId
	| typeof maintenanceTabId
	| typeof conditionalAccessTabId;

const settingsTabId = "settings";
const whoHasAccessTabId = "who-has-access";
const ownersTabId = "owners";
const maintenanceTabId = "maintenance";
const conditionalAccessTabId = "conditional-access";

export function initialGenericState(
	initialModel: EditAppSettings<EditGenericAppViewModel>
): SaveGenericAppModel {
	const {
		target,
		model: {
			app
		}
	} = initialModel;

	let saveTarget: SaveAppTargetModel;

	switch (target.kind) {
		case "installed-app":
			saveTarget = {
				type: SaveAppTargetType.ExistingInstalledApp,
				installedAppId: target.app.installedAppId
			};
			break;
		case "new-app-from-main":
			saveTarget = {
				type: SaveAppTargetType.NewAppFromMain,
				mainAppId: target.app.mainAppId
			};
			break;
		case "new-environment-app":
			saveTarget = {
				type: SaveAppTargetType.NewEnvironmentOnlyApp
			};
			break;
		default:
			never(target);
	}

	return {
		target: saveTarget,
		color: app.color.kind === AppColorKind.Custom
			? { kind: app.color.kind, value: app.color.value }
			: { kind: app.color.kind },
		image: app.image,
		name: app.name,
		everyoneHasAccess: app.everyoneHasAccess,
		whoHasAccess: app.whoHasAccess,
		owners: app.owners,
		tags: app.tags
			.map((tag: TagViewModel): TagSaveModel => ({
				id: tag.id,
				name: tag.name
			}))
	};
}

export function prepareGenericPropertiesForSaving<TModel extends SaveGenericAppModel>(model: TModel): TModel {
	return {
		...model,
		whoHasAccess: model.everyoneHasAccess
			? null
			: model.whoHasAccess
	};
};

export interface EditAppSettings<TAppModel extends EditGenericAppViewModel> {
	target: AppTarget;
	model: EditAppModel<TAppModel>;
}
